import { array } from '@ember/helper';
import { Button } from 'tio-ui/components/buttons';
import { Form, Input, type FormResultData } from 'tio-ui/components/forms';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack } from 'tio-ui/components/layout';
import * as v from 'valibot';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import LoginHere from 'tio-employee/components/auth/login-here';
import pageTitle from 'ember-page-title/helpers/page-title';
import TioFooter from 'tio-common/components/tio/footer';
import TioLoadingMessages from 'tio-common/components/tio/loading-messages';
import type { Touched } from 'tio-common/helpers/errors-for-valibot-field';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

const formSchema = v.object({
  work_email: v.pipe(
    v.string(),
    v.trim(),
    v.nonEmpty('* Required'),
    v.email('The Email field must be a valid email')
  ),
  first_name: v.pipe(v.string(), v.trim(), v.nonEmpty('* Required')),
  last_name: v.pipe(v.string(), v.trim(), v.nonEmpty('* Required')),
});

interface SelfRegistrationFormData {
  work_email: string;
  first_name: string;
  last_name: string;
}

export default class SelfRegistrationRouteComponent extends Component {
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked isSubmitting = false;
  @tracked formSubmitted = false;

  @tracked formValidationErrors: Record<string, string[]> = {};
  @tracked touched: Touched = {
    work_email: false,
    first_name: false,
    last_name: false,
  };
  @tracked formData = {
    work_email: '',
    first_name: '',
    last_name: '',
  };

  validateField = (data: SelfRegistrationFormData | Record<string, unknown>) => {
    const result = v.safeParse(formSchema, data);
    if (result.success) {
      this.formValidationErrors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};
      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
    }
  };

  onFormChange = (_result: FormResultData<SelfRegistrationFormData>, _event: Event) => {
    this.validateField(_result.data);
    const target = _event.target as HTMLInputElement | null;
    if (target?.name && target.name in this.touched) {
      this.touched = { ...this.touched, [target.name]: true };
    }
    this.formData = { ...this.formData, ..._result.data };
  };

  onFormSubmit = (_result: FormResultData<SelfRegistrationFormData>, _event: SubmitEvent) => {
    this.validateField(_result.data);
    this.touched = {
      work_email: true,
      first_name: true,
      last_name: true,
    };
    this.onSubmit(_result.data);
  };

  closeDialog = () => {
    this.router.transitionTo('login');
  };

  onSubmit = async ({
    work_email,
    first_name,
    last_name,
  }: {
    work_email: string;
    first_name: string;
    last_name: string;
  }) => {
    this.isSubmitting = true;
    try {
      await this.store
        .adapterFor('employee')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .selfRegister({ email: work_email, first_name, last_name });
    } catch (e) {
      console.error(e);
    } finally {
      this.isSubmitting = false;
      this.formSubmitted = true;
    }
  };

  <template>
    {{pageTitle (t "login.register")}}
    <div class="login flex flex-col gap-y-10 justify-center items-center mt-20">
      <div class="bg-white w-full max-w-xl shadow-2xl p-5">
        <div class="text-center mb-6 text-lg">
          {{t "login.what_email"}}
          {{t "login.will_send_email"}}
        </div>
        <Form @onChange={{this.onFormChange}} @onSubmit={{this.onFormSubmit}}>
          <VStack>
            <Input
              @name="work_email"
              @type="email"
              @label="Email"
              @value={{this.formData.work_email}}
              @description={{t "login.email_hint_self_registration"}}
              @isRequired={{true}}
              @errors={{errorsForValibotField
                "work_email"
                schemaErrors=this.formValidationErrors
                touched=this.touched
              }}
            />
            <Input
              @name="first_name"
              @label="First Name"
              @value={{this.formData.first_name}}
              @isRequired={{true}}
              @errors={{errorsForValibotField
                "first_name"
                schemaErrors=this.formValidationErrors
                touched=this.touched
              }}
            />
            <Input
              @name="last_name"
              @label="Last Name"
              @value={{this.formData.last_name}}
              @isRequired={{true}}
              @errors={{errorsForValibotField
                "last_name"
                schemaErrors=this.formValidationErrors
                touched=this.touched
              }}
            />
            <div class="text-center">
              <Button type="submit" @intent="primary">
                {{t "login.submit"}}
              </Button>
            </div>
            {{#if this.isSubmitting}}
              <div class="flex justify-center">
                <TioLoadingMessages
                  @i18nPath="login.loading_messages"
                  @messageKeys={{array
                    "verify_credentials"
                    "wait_for_system_warmup"
                    "thank_you_for_your_patience"
                    "fetch_company_data"
                  }}
                  @initialIntervalMs={{0}}
                  @messageIntervalMs={{3000}}
                />
              </div>
            {{/if}}
          </VStack>
        </Form>
        <LoginHere />
      </div>
    </div>
    <TioFooter />

    <Modal @isOpen={{this.formSubmitted}} @onClose={{this.closeDialog}} class="min-h-64">
      <:header>
        <div class="flex justify-center mt-4">
          {{t "login.action_required"}}
        </div>
      </:header>
      <:body>
        <p>{{t "login.self_registration_success"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.closeDialog}}
          @submitText={{t "common.close"}}
        />
      </:footer>
    </Modal>
  </template>
}
